import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { X, Envelope, HardDrives, Shield, CheckCircle, CaretLeft, CaretRight } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StepIndicator } from '@/components/ui/StepIndicator'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { DotLoader } from '@/components/ui/DotLoader'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { useScrollLock } from '@/lib/useScrollLock'
import { checkboxInputClass, checkboxLabelClass } from '@/lib/styleKit'
import { cn, isValidEmail } from '@/lib/utils'
import { senderEmailsApi } from '@/lib/api'
import type { DetectedProvider } from '@/types/senderEmail'

interface Props {
  onClose: () => void
  onDirtyChange?: (dirty: boolean) => void
}

const STEPS: { key: number; label: string }[] = [
  { key: 1, label: 'Basic info' },
  { key: 2, label: 'SMTP' },
  { key: 3, label: 'IMAP' },
  { key: 4, label: 'Review' },
]

/**
 * Field chrome shared by this modal and `EditSenderEmailModal` — declared here
 * once and imported there. The sender-email pair is the only place in the
 * product using a 13px sentence-case field label instead of the kit's
 * uppercase eyebrow (`labelClass`), so it stays out of `styleKit.ts`.
 */
export const fieldLabel = 'block mb-1.5 text-[13px] font-medium text-foreground dark:text-[#f7f8f8]'
/** Kit checkbox label at this modal pair's 13px body size. */
export const checkboxLabel = cn(checkboxLabelClass, 'text-[13px]')

const helperText = 'mt-1.5 text-[12px] text-muted-foreground dark:text-[#8a8f98] leading-relaxed'

/**
 * TCP port sanity for the SMTP/IMAP port fields. A cleared number input
 * coerces to 0 (`Number('')`), so an emptied port field fails this too.
 */
export const isValidPort = (port: number) => Number.isInteger(port) && port >= 1 && port <= 65535

/**
 * The port fields are plain text inputs, not `type="number"`: the spinner
 * arrows and scroll-wheel stepping on a number input changed ports by
 * accident, and browsers let letters like "e" through. Digits only, at most
 * five of them, and an empty field is 0 so isValidPort rejects it the same
 * way it rejected a cleared number input.
 */
export const parsePortInput = (raw: string): number => {
  const digits = raw.replace(/\D/g, '').slice(0, 5)
  return digits ? Number(digits) : 0
}

/** Props shared by every port field: text, numeric keyboard on phones. */
export const portInputProps = {
  type: 'text',
  inputMode: 'numeric',
  pattern: '[0-9]*',
  autoComplete: 'off',
} as const

/**
 * Red required-field marker (palette red, dual-mode). `aria-hidden` because the
 * requirement itself is announced by the field's own `required` attribute — a
 * screen reader reading "Label star" is noise, not information (§56).
 */
const Req: React.FC = () => <span aria-hidden="true" className="text-red-500 dark:text-red-400"> *</span>

const GoogleIcon: React.FC = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
)

const MicrosoftIcon: React.FC = () => (
  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
)

/**
 * The three ways to connect a mailbox, presented as equal-weight picker
 * cards rather than "two OAuth buttons, then a divider, then a form" —
 * Google/Microsoft need nothing beyond the click (the address comes back
 * from the OAuth grant), while "Any Provider" is the one that leads
 * somewhere on THIS page: picking it is what reveals the Label/Email
 * fields below, rather than those fields always being there regardless of
 * which path is chosen.
 */
const ProviderCard: React.FC<{
  icon: React.ReactNode
  label: string
  sublabel: string
  selected: boolean
  loading?: boolean
  disabled?: boolean
  onClick: () => void
}> = ({ icon, label, sublabel, selected, loading, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    className={cn(
      'w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150',
      'bg-muted/30 border-border/50 hover:bg-muted/50',
      'dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04]',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/40',
      selected && 'border-[#5e6ad2] dark:border-[#7170ff] ring-1 ring-[#5e6ad2]/30 dark:ring-[#7170ff]/30',
      disabled && 'opacity-60 pointer-events-none',
    )}
  >
    <span className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border border-black/[0.06] dark:bg-white/[0.06] dark:border-white/[0.08]">
      {loading ? <DotLoader size="sm" announce={false} /> : icon}
    </span>
    <span className="min-w-0">
      <span className="block text-[12px] text-muted-foreground dark:text-[#8a8f98]">{label}</span>
      <span className="block text-[14px] font-semibold text-foreground dark:text-[#f7f8f8] truncate">{sublabel}</span>
    </span>
  </button>
)

/** Everything inside the dialog a keyboard user can land on (§16 focus trap). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

/** Outcome of an async submit, surfaced through a `role="status"` live region. */
export interface DialogOutcome {
  text: string
  error?: boolean
}

/** Live-region chrome for `DialogOutcome`. Collapses to zero height when idle. */
export const outcomeClass = (outcome: DialogOutcome | null) =>
  cn(
    'px-6 text-[12px] leading-relaxed',
    outcome && 'pb-3',
    outcome?.error
      ? 'text-red-600 dark:text-red-400'
      : 'text-muted-foreground dark:text-[#8a8f98]',
  )

/**
 * The §16 dialog contract shared by this modal and `EditSenderEmailModal`:
 * initial focus, a Tab/Shift-Tab trap, focus restore to the trigger on unmount,
 * and Escape routed through the caller's dismissal guard.
 *
 * Declared here rather than in `lib/` for the same reason `fieldLabel` is: the
 * sender-email modal pair is its only consumer. `ConfirmDialog` owns the same
 * contract for confirmations; neither modal may reuse it because both hold
 * multi-field unsaved state and therefore need a *guarded* dismissal.
 */
export function useDialogChrome(
  surfaceRef: React.RefObject<HTMLElement>,
  requestClose: () => void,
  initialFocusRef?: React.RefObject<HTMLElement>,
): void {
  // Latest-callback ref. `requestClose` closes over the dirty snapshot and is a
  // new function on every keystroke; reading it through a ref keeps the key
  // listener attached exactly once while never invoking a stale copy.
  const closeRef = useRef(requestClose)
  useEffect(() => {
    closeRef.current = requestClose
  }, [requestClose])

  // Initial focus + focus restore (§16). The trigger may have unmounted while
  // the dialog was open (a deleted row takes its own menu button with it), so
  // `isConnected` guards the hand-back.
  useEffect(() => {
    const previous = (document.activeElement as HTMLElement | null) ?? null
    ;(initialFocusRef?.current ?? surfaceRef.current)?.focus()
    return () => {
      if (previous?.isConnected && typeof previous.focus === 'function') previous.focus()
    }
    // Mount/unmount only — refs are stable and re-running would steal focus
    // back to the first field on every render.

  }, [])

  // Key handling is scoped to the dialog node, never `window` (§57). A nested
  // `ConfirmDialog` is portalled to `document.body` *outside* this node, so its
  // own Escape/Tab handling can never double-fire with ours.
  useEffect(() => {
    const node = surfaceRef.current
    if (!node) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // We own dismissal while we're the top layer.
        e.stopPropagation()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab') return

      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      // Tab never leaves the dialog. The empty case is the defensive floor:
      // swallow the key regardless so the trap holds with nothing to land on.
      e.preventDefault()
      e.stopPropagation()
      if (focusables.length === 0) return
      const current = focusables.indexOf(document.activeElement as HTMLElement)
      const next =
        current === -1
          ? e.shiftKey
            ? focusables[focusables.length - 1]
            : focusables[0]
          : e.shiftKey
            ? focusables[(current === 0 ? focusables.length : current) - 1]
            : focusables[(current + 1) % focusables.length]
      next?.focus()
    }

    node.addEventListener('keydown', onKey)
    return () => {
      node.removeEventListener('keydown', onKey)
    }
  }, [surfaceRef])
}

const AddSenderEmailModal: React.FC<Props> = ({ onClose, onDirtyChange }) => {
  useScrollLock(true)
  const { createSenderEmail, verifySenderEmail } = useSenderEmailStore()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<DialogOutcome | null>(null)
  const [showDiscard, setShowDiscard] = useState(false)

  const surfaceRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`
  const fid = (name: string) => `${uid}-${name}`

  // Step 1: Basic Info
  const [label, setLabel] = useState('')
  const [emailAddress, setEmailAddress] = useState('')

  // Step 2: SMTP. TLS/STARTTLS is the sensible default for every provider this
  // form targets (port 587) — not asked, so there's nothing to get wrong here.
  const [smtpServer, setSmtpServer] = useState('')
  const [smtpPort, setSmtpPort] = useState(587)
  const [smtpUsername, setSmtpUsername] = useState('')
  const [smtpPassword, setSmtpPassword] = useState('')

  // Step 3: IMAP (optional). Username/password are almost always identical to
  // the SMTP ones for the same mailbox, so that's the default and nothing is
  // asked for them unless the user says otherwise — only the host/port
  // (which do genuinely differ, e.g. imap.gmail.com vs smtp.gmail.com) and
  // the monitoring toggle are asked up front.
  const [imapServer, setImapServer] = useState('')
  const [imapPort, setImapPort] = useState(993)
  // SMTP transport. 385c1d4 removed these in favour of a hardcoded STARTTLS,
  // on the grounds that nobody needed to choose -- but a fixed default cannot
  // serve both conventions: 587 wants STARTTLS and 465 wants implicit TLS, and
  // providers split across the two (Office 365 is 587-only, GoDaddy is 465).
  // With no control, a mailbox on the other convention could not be added.
  const [smtpUseTls, setSmtpUseTls] = useState(true)
  const [smtpUseSsl, setSmtpUseSsl] = useState(false)
  const [imapSameAsSmtp, setImapSameAsSmtp] = useState(true)
  // IMAP transport. Separate from the SMTP pair because the protocols disagree
  // on both the mechanism and the port: SMTP is implicit TLS on 465 / STARTTLS
  // on 587, IMAP is implicit TLS on 993 / STARTTLS on 143. Every IMAP
  // connection used to be hardcoded to implicit TLS, so a server expecting
  // STARTTLS on 143 simply could not be added.
  //
  // Kept when the wizard came back and the SMTP TLS/SSL checkboxes were
  // dropped as "not required": these are the IMAP pair, they were asked for
  // specifically, and unlike the SMTP ones there is no working default that
  // covers both kinds of server.
  const [imapUseSsl, setImapUseSsl] = useState(true)
  const [imapUseStarttls, setImapUseStarttls] = useState(false)
  const [imapUsername, setImapUsername] = useState('')
  const [imapPassword, setImapPassword] = useState('')
  const [monitoringEnabled, setMonitoringEnabled] = useState(true)

  // Step 4: Review
  //
  // Nothing about the limit is decided here, or asked of the user. The backend
  // works the provider out from the SMTP hostname and resolves the cap from it
  // (core/smtp_providers.py). This call is a PREVIEW of that answer so the
  // review step can show what is about to be stored; the create route detects
  // again from its own payload and never trusts what came back through here.
  const [detected, setDetected] = useState<DetectedProvider | null>(null)
  // What the user says they pasted into the password field. Recorded only --
  // authentication is identical either way -- so a rejected credential can be
  // explained correctly (core/smtp_providers.app_password_advice). Suggested
  // from the detected provider until the user decides for themselves; the ref
  // is what stops the suggestion overwriting their choice.
  const [usesAppPassword, setUsesAppPassword] = useState(false)
  const appPasswordTouched = useRef(false)
  // Only used when the host matched nothing. The backend ignores it otherwise,
  // so it is never sent for a recognised provider.
  const [customLimit, setCustomLimit] = useState(500)
  // Asked for whenever the cap is unknown: an unmatched host, or a relay whose
  // limit is set by the customer's plan rather than by the provider.
  const needsCustomLimit = detected !== null && !detected.limit_known

  // One preview per host+address, so walking step 2 -> 3 -> 4 does not re-ask.
  const lastDetectedFor = useRef('')
  useEffect(() => {
    // From step 2, where the password is typed, rather than only at the review
    // step: the app-password guidance under the password field has to be there
    // BEFORE the user types, which is the whole point of it.
    if (step < 2 || !smtpServer.trim()) return
    const key = `${smtpServer.trim()}|${emailAddress.trim()}`
    if (key === lastDetectedFor.current) return
    lastDetectedFor.current = key
    // Staleness is decided by the KEY, not by an effect-scoped flag: the user
    // walks on to step 3 while this is still in flight, which re-runs the
    // effect and would fire a cleanup that discarded the answer we asked for.
    // Comparing the key instead drops only a response whose host or address is
    // no longer the one on screen.
    senderEmailsApi.detectProvider(smtpServer.trim(), emailAddress.trim())
      .then(res => {
        if (lastDetectedFor.current !== key) return
        setDetected(res.data)
        // Seed the fallback with the conservative default the backend would
        // otherwise apply, so leaving it alone changes nothing.
        setCustomLimit(res.data.daily_limit)
        // Suggest what the provider forces, but never overrule the user.
        if (!appPasswordTouched.current) {
          setUsesAppPassword(res.data.app_password === 'required')
        }
      })
      // A failed preview costs the form a line of guidance, not its function:
      // the server detects independently on create either way. Clearing the
      // key lets a later step retry rather than caching the failure.
      .catch(() => {
        if (lastDetectedFor.current !== key) return
        lastDetectedFor.current = ''
        setDetected(null)
      })
  }, [step, smtpServer, emailAddress])

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify([
        label, emailAddress,
        smtpServer, smtpPort, smtpUsername, smtpPassword, smtpUseTls, smtpUseSsl,
        imapServer, imapPort, imapSameAsSmtp, imapUsername, imapPassword, monitoringEnabled,
        imapUseSsl, imapUseStarttls, usesAppPassword,
      ]),
    [
      label, emailAddress,
      smtpServer, smtpPort, smtpUsername, smtpPassword, smtpUseTls, smtpUseSsl,
      imapServer, imapPort, imapSameAsSmtp, imapUsername, imapPassword, monitoringEnabled,
      imapUseSsl, imapUseStarttls, usesAppPassword,
    ],
  )
  // Snapshot of the values the modal opened with (§18; same `savedValues`
  // idiom as `CampaignDefaultsSection`). `useRef` seeds once on mount, so the
  // dirty test can never drift as the field defaults change.
  const initialSnapshotRef = useRef(currentSnapshot)
  const isDirty = currentSnapshot !== initialSnapshotRef.current

  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  /**
   * Dismissal guard (§19 / §18). The backdrop is deliberately inert — this is a
   * four-step form and an outside click would silently drop everything typed.
   * Escape, the X and Cancel all land here instead: untouched form closes
   * immediately, dirty form goes through the discard confirmation.
   */
  const requestClose = () => {
    if (isSubmitting) return
    if (isDirty) {
      setShowDiscard(true)
      return
    }
    onClose()
  }

  const canProceed = () => {
    switch (step) {
      case 1: return Boolean(label.trim()) && isValidEmail(emailAddress.trim())
      case 2: return Boolean(smtpServer.trim() && smtpUsername.trim() && smtpPassword.trim()) && isValidPort(smtpPort)
      // IMAP is optional, but a filled-in server needs a real port. Custom
      // IMAP credentials, when shown, are optional too (an empty one still
      // falls back to the SMTP credential server-side).
      case 3: return !imapServer.trim() || isValidPort(imapPort)
      // The only editable thing here is the fallback limit, and only for a
      // host we could not identify.
      case 4: return !needsCustomLimit
        || (Number.isInteger(customLimit)
            && customLimit >= 1
            && customLimit <= (detected?.max_custom_limit ?? 100000))
      default: return false
    }
  }

  // Nylas OAuth mailbox connection (F8) — a full-page redirect (not a popup):
  // simpler, no popup-blocker edge cases, matches how this codebase's WorkOS
  // SSO flow already redirects instead of using a popup.
  const [oauthConnecting, setOauthConnecting] = useState<'google' | 'microsoft' | null>(null)
  // Each provider's button is independently toggleable server-side
  // (NYLAS_GOOGLE_CONNECT_ENABLED / NYLAS_MICROSOFT_CONNECT_ENABLED), so ops
  // can hide one without turning off Nylas entirely. `null` (not yet loaded)
  // renders neither button — an optimistic "show both" default caused a
  // visible flash of a disabled provider's button before the real flags
  // arrived and it got pulled again. Fails open (both shown) on a fetch
  // error rather than permanently hiding a working connect path.
  const [oauthAvailable, setOauthAvailable] = useState<{ google: boolean; microsoft: boolean } | null>(null)
  useEffect(() => {
    let cancelled = false
    senderEmailsApi.getOAuthProviders()
      .then(res => { if (!cancelled) setOauthAvailable(res.data) })
      .catch(() => { if (!cancelled) setOauthAvailable({ google: true, microsoft: true }) })
    return () => { cancelled = true }
  }, [])
  // Which provider card is picked. Google/Microsoft lead off the page (a
  // redirect), so this mostly just tracks "manual" — the only choice that
  // stays on this step and reveals the Label/Email fields below it.
  const [connectMethod, setConnectMethod] = useState<'google' | 'microsoft' | 'manual' | null>(null)
  const handleOAuthConnect = async (provider: 'google' | 'microsoft') => {
    setConnectMethod(provider)
    setOauthConnecting(provider)
    try {
      const res = await senderEmailsApi.oauthAuthorize(provider)
      window.location.assign(res.data.authorize_url)
    } catch {
      setOauthConnecting(null)
      setOutcome({
        text: 'Could not start the connection. Please try again.',
        error: true,
      })
    }
  }

  const handleSubmit = async () => {
    // In-flight guard. `Button` blocks a second click while `loading`, but
    // Enter-on-a-field and the keyboard paths reach here directly, and a
    // double-POST would create two accounts.
    if (isSubmitting) return
    setIsSubmitting(true)
    setOutcome({ text: 'Creating the email account and verifying the connection…' })
    try {
      const sender = await createSenderEmail({
        label,
        email_address: emailAddress,
        smtp_server: smtpServer,
        smtp_port: smtpPort,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword,
        smtp_use_tls: smtpUseTls,
        smtp_use_ssl: smtpUseSsl,
        imap_server: imapServer || undefined,
        imap_port: imapPort || undefined,
        imap_use_ssl: imapUseSsl,
        imap_use_starttls: imapUseStarttls,
        // "Same as SMTP" sends nothing — the backend already fills IMAP
        // creds from the SMTP ones whenever they're absent
        // (services/sender_email.py), so leaving these unset is exactly the
        // same outcome, not a shortcut around it.
        //
        // The transport pair above is NOT covered by that fallback: the
        // backend cannot infer from SMTP which of 993/143 this mailbox wants,
        // which is the whole reason these columns exist. Always sent.
        imap_username: imapSameAsSmtp ? undefined : (imapUsername || undefined),
        imap_password: imapSameAsSmtp ? undefined : (imapPassword || undefined),
        monitoring_enabled: monitoringEnabled,
        uses_app_password: usesAppPassword,
        // Sent only when the host matched nothing; the backend ignores it for a
        // recognised provider, so including it there would be noise at best.
        ...(needsCustomLimit ? { daily_send_limit: customLimit } : {}),
      })

      // Auto-verify after creation
      try {
        await verifySenderEmail(sender.id)
      } catch {
        // Non-critical: verification can be done later
      }

      setOutcome({ text: 'Email account created.' })
      onClose()
    } catch {
      // Store owns the toast; the live region owns the announcement.
      setOutcome({
        text: 'Could not create the email account. Check the details and try again.',
        error: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useDialogChrome(surfaceRef, requestClose, firstFieldRef)

  const stepIcon =
    step === 1 ? Envelope : step === 2 ? HardDrives : step === 3 ? Shield : CheckCircle
  const StepIcon = stepIcon
  const stepTitle =
    step === 1 ? 'Basic Information'
      : step === 2 ? 'SMTP Configuration'
      : step === 3 ? 'IMAP Configuration (Optional)'
      : 'Review & Create'

  if (typeof document === 'undefined') return null

  // Portalled to `document.body` (§29). `AnimatePresence` lives in
  // `SenderEmailsPage` — it must sit *outside* the conditional that mounts this
  // component or the exit variant never plays. PresenceContext travels the
  // React tree, not the DOM tree, so these `motion.div`s still animate out from
  // inside the portal.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
        {/* Backdrop — blur-only, no dark overlay (§45). No click-to-close: this
            is a multi-step form and an outside click would silently discard
            unsaved input. Close via Cancel, the X or Escape. */}
        <motion.div
          className="fixed top-0 left-0 w-screen h-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          aria-hidden="true"
        />
        <motion.div
          ref={surfaceRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          // Focusable-but-not-tabbable: clicking dead space inside the dialog
          // lands here rather than on <body>, so the scoped keydown listener
          // keeps seeing Escape and Tab.
          tabIndex={-1}
          className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl outline-none bg-card border border-border/60 shadow-2xl dark:bg-[#191a1b] dark:border-white/[0.08]"
          initial={{ scale: 0.96, opacity: 0, y: 8 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-border/50 dark:border-white/[0.06]">
            <div className="min-w-0">
              <h2
                id={titleId}
                className="text-foreground dark:text-[#f7f8f8] truncate"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.2px' }}
              >
                Add Email Account
              </h2>
              <p id={descId} className="mt-0.5 text-[13px] text-muted-foreground dark:text-[#8a8f98]">
                Step {step} of 4
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="h-8 w-8 -mr-1 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150 flex-shrink-0"
            >
              <X className="h-4 w-4" weight="bold" />
            </button>
          </div>

          {/* Step breadcrumb */}
          <div className="px-6 pt-4">
            <StepIndicator steps={STEPS} current={step} className="flex-wrap" />
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {/* Section header — icon chip + title */}
            <div className="flex items-center gap-2.5 mb-4">
              <div className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25">
                <StepIcon className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8]"
                style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.16px' }}
              >
                {stepTitle}
              </h3>
            </div>

            {step === 1 && (
              <div className="space-y-4">
                {/* Three equal-weight ways to connect (F8), not "two OAuth
                    buttons then a form": Google/Microsoft need nothing past
                    the click (the address comes back from the OAuth grant);
                    "Any Provider" is the one that stays on this page, and
                    picking it is what reveals Label/Email below. Each OAuth
                    card is independently toggleable server-side. */}
                <div className="space-y-2.5">
                  {oauthAvailable?.google && (
                    <ProviderCard
                      icon={<GoogleIcon />}
                      label="Google"
                      sublabel="Gmail / G-Suite"
                      selected={connectMethod === 'google'}
                      loading={oauthConnecting === 'google'}
                      disabled={oauthConnecting !== null}
                      onClick={() => handleOAuthConnect('google')}
                    />
                  )}
                  {oauthAvailable?.microsoft && (
                    <ProviderCard
                      icon={<MicrosoftIcon />}
                      label="Microsoft"
                      sublabel="Office 365 / Outlook"
                      selected={connectMethod === 'microsoft'}
                      loading={oauthConnecting === 'microsoft'}
                      disabled={oauthConnecting !== null}
                      onClick={() => handleOAuthConnect('microsoft')}
                    />
                  )}
                  <ProviderCard
                    icon={<Envelope className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />}
                    label="Any Provider"
                    sublabel="IMAP / SMTP"
                    selected={connectMethod === 'manual'}
                    disabled={oauthConnecting !== null}
                    onClick={() => setConnectMethod('manual')}
                  />
                </div>

                {connectMethod === 'manual' && (
                  <>
                    <div>
                      <label htmlFor={fid('label')} className={fieldLabel}>Label<Req /></label>
                      <Input
                        id={fid('label')}
                        ref={firstFieldRef}
                        required
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g. Sales Team, Support"
                      />
                    </div>
                    <div>
                      <label htmlFor={fid('email')} className={fieldLabel}>Email Address<Req /></label>
                      <Input
                        id={fid('email')}
                        required
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        placeholder="e.g. sales@yourcompany.com"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label htmlFor={fid('smtp-server')} className={fieldLabel}>SMTP Server<Req /></label>
                    <Input
                      id={fid('smtp-server')}
                      required
                      value={smtpServer}
                      onChange={(e) => setSmtpServer(e.target.value)}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label htmlFor={fid('smtp-port')} className={fieldLabel}>Port<Req /></label>
                    <Input
                      id={fid('smtp-port')}
                      required
                      {...portInputProps}
                      value={smtpPort || ''}
                      onChange={(e) => setSmtpPort(parsePortInput(e.target.value))}
                    />
                  </div>
                </div>
                {/* Mutually exclusive, and each moves the port to its own
                    convention: STARTTLS on 587, implicit TLS on 465. Getting
                    that pairing wrong is the usual cause of a verification
                    timeout -- Exchange Online, for one, has nothing listening
                    on 465 at all. */}
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={smtpUseTls}
                      onChange={(e) => {
                        setSmtpUseTls(e.target.checked)
                        if (e.target.checked) { setSmtpUseSsl(false); setSmtpPort(587) }
                      }}
                      className={checkboxInputClass}
                    />
                    Use STARTTLS (port 587)
                  </label>
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={smtpUseSsl}
                      onChange={(e) => {
                        setSmtpUseSsl(e.target.checked)
                        if (e.target.checked) { setSmtpUseTls(false); setSmtpPort(465) }
                      }}
                      className={checkboxInputClass}
                    />
                    Use SSL (implicit TLS, port 465)
                  </label>
                </div>
                <div>
                  <label htmlFor={fid('smtp-username')} className={fieldLabel}>Username<Req /></label>
                  <Input
                    id={fid('smtp-username')}
                    required
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div>
                  <label htmlFor={fid('smtp-password')} className={fieldLabel}>Password<Req /></label>
                  <Input
                    id={fid('smtp-password')}
                    required
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder="App password or SMTP password"
                  />
                  {/* An app password is an ACCOUNT credential, not a protocol
                      one: the same string is what SMTP and IMAP both
                      authenticate with, so there is one box, not one per
                      protocol. Ticking it changes nothing about how we
                      connect; it is what lets a rejected credential come back
                      with the right next step. */}
                  <label className={`${checkboxLabel} mt-2.5`}>
                    <input
                      type="checkbox"
                      checked={usesAppPassword}
                      onChange={(e) => {
                        appPasswordTouched.current = true
                        setUsesAppPassword(e.target.checked)
                      }}
                      className={checkboxInputClass}
                    />
                    This is an app password
                  </label>
                  {detected?.app_password_hint ? (
                    <p className={helperText}>
                      {detected.app_password_hint}
                      {detected.app_password_url ? (
                        <>
                          {' '}
                          <a
                            href={detected.app_password_url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline underline-offset-2"
                          >
                            Create one
                          </a>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <p className="text-[13px] text-muted-foreground dark:text-[#8a8f98] leading-relaxed">
                  IMAP is used for mailbox monitoring and reading replies. If left empty, SMTP settings will be used.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label htmlFor={fid('imap-server')} className={fieldLabel}>IMAP Server</label>
                    <Input
                      id={fid('imap-server')}
                      value={imapServer}
                      onChange={(e) => setImapServer(e.target.value)}
                      placeholder={smtpServer || 'imap.gmail.com'}
                    />
                  </div>
                  <div>
                    <label htmlFor={fid('imap-port')} className={fieldLabel}>Port</label>
                    <Input
                      id={fid('imap-port')}
                      {...portInputProps}
                      value={imapPort || ''}
                      onChange={(e) => setImapPort(parsePortInput(e.target.value))}
                    />
                  </div>
                </div>
                {/* Transport. Mutually exclusive -- a socket is either wrapped
                    in TLS from the first byte or upgraded afterwards, never
                    both -- and each moves the port to its own convention,
                    which is the pairing people get wrong.

                    Unlike the SMTP TLS/SSL checkboxes this wizard dropped as
                    "not required", there is no default that covers both kinds
                    of IMAP server: 993 and 143 want different mechanisms, and
                    guessing wrong means the mailbox cannot be polled at all. */}
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={imapUseSsl}
                      onChange={(e) => {
                        setImapUseSsl(e.target.checked)
                        if (e.target.checked) { setImapUseStarttls(false); setImapPort(993) }
                      }}
                      className={checkboxInputClass}
                    />
                    Use SSL (implicit TLS, port 993)
                  </label>
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={imapUseStarttls}
                      onChange={(e) => {
                        setImapUseStarttls(e.target.checked)
                        if (e.target.checked) { setImapUseSsl(false); setImapPort(143) }
                      }}
                      className={checkboxInputClass}
                    />
                    Use STARTTLS (port 143)
                  </label>
                </div>
                <label className={checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={imapSameAsSmtp}
                    onChange={(e) => {
                      setImapSameAsSmtp(e.target.checked)
                      // Re-checking after typing custom creds must not leave
                      // them sitting in state to be submitted silently later.
                      if (e.target.checked) { setImapUsername(''); setImapPassword('') }
                    }}
                    className={checkboxInputClass}
                  />
                  Use the same username and password as SMTP
                </label>
                {!imapSameAsSmtp && (
                  <>
                    <div>
                      <label htmlFor={fid('imap-username')} className={fieldLabel}>IMAP Username</label>
                      <Input
                        id={fid('imap-username')}
                        value={imapUsername}
                        onChange={(e) => setImapUsername(e.target.value)}
                        placeholder={smtpUsername || 'e.g. your-email@gmail.com'}
                      />
                    </div>
                    <div>
                      <label htmlFor={fid('imap-password')} className={fieldLabel}>IMAP Password</label>
                      <Input
                        id={fid('imap-password')}
                        type="password"
                        value={imapPassword}
                        onChange={(e) => setImapPassword(e.target.value)}
                        placeholder="Leave empty to use the SMTP password"
                      />
                    </div>
                  </>
                )}
                <label className={checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={monitoringEnabled}
                    onChange={(e) => setMonitoringEnabled(e.target.checked)}
                    className={checkboxInputClass}
                  />
                  Enable mailbox monitoring (auto-detect replies)
                </label>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div className="rounded-xl p-5 space-y-3 bg-muted/50 border border-border/40 dark:bg-white/[0.03] dark:border-white/[0.06]">
                  {[
                    { k: 'Label', v: label },
                    { k: 'Email', v: emailAddress },
                    { k: 'SMTP', v: `${smtpServer}:${smtpPort}` },
                    { k: 'IMAP', v: `${imapServer || smtpServer}:${imapPort || 993}` },
                    { k: 'Monitoring', v: monitoringEnabled ? 'Enabled' : 'Disabled' },
                  ].map((row) => (
                    <div key={row.k} className="flex items-center justify-between gap-3 text-[13px]">
                      <span className="text-muted-foreground dark:text-[#8a8f98]">{row.k}</span>
                      <span className="font-medium text-foreground dark:text-[#f7f8f8] truncate text-right">{row.v}</span>
                    </div>
                  ))}
                </div>
                {/* Detected from the SMTP host, not asked and not editable.
                    Users do not know their provider's cap, and a typed guess
                    that runs high is what gets a mailbox throttled. */}
                <div className="flex items-center justify-between gap-3 text-[13px]">
                  <span className="text-muted-foreground dark:text-[#8a8f98]">Provider</span>
                  <span className="font-medium text-foreground dark:text-[#f7f8f8] truncate text-right">
                    {detected?.label ?? '—'}
                  </span>
                </div>
                {/* Editable ONLY for a host we could not identify: there is no
                    published figure to look up, and whoever runs a self-hosted
                    or corporate relay knows its cap. For a recognised provider
                    this stays read-only, and the backend ignores the field
                    regardless of what is sent. */}
                {needsCustomLimit ? (
                  <div>
                    <label htmlFor={fid('daily-limit')} className={fieldLabel}>Daily send limit</label>
                    <Input
                      id={fid('daily-limit')}
                      aria-describedby={fid('daily-limit-help')}
                      type="number"
                      min={1}
                      max={detected?.max_custom_limit}
                      value={customLimit}
                      onChange={(e) => setCustomLimit(Number(e.target.value))}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 text-[13px]">
                    <span className="text-muted-foreground dark:text-[#8a8f98]">Daily send limit</span>
                    <span className="font-medium text-foreground dark:text-[#f7f8f8]">
                      {detected ? `${detected.daily_limit.toLocaleString()}/day` : '—'}
                    </span>
                  </div>
                )}
                <p id={fid('daily-limit-help')} className={helperText}>
                  {needsCustomLimit
                    ? `${detected?.recognised
                          ? `${detected.note} Set the limit for your plan`
                          : "We didn't recognise this SMTP host, so set its daily limit yourself"
                       } (1–${detected?.max_custom_limit.toLocaleString()}). Leave it as is if you're unsure. `
                    : detected ? `${detected.note} ` : ''}
                  Connection will be verified automatically after creation.
                </p>
              </div>
            )}
          </div>

          {/* Outcome of the async submit — announced. `role="status"` is present
              from first render so the live region exists before it fills. */}
          <p role="status" className={outcomeClass(outcome)}>{outcome?.text ?? ''}</p>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border/50 dark:border-white/[0.06]">
            <Button
              variant="outline"
              onClick={() => step > 1 ? setStep(step - 1) : requestClose()}
            >
              <CaretLeft className="h-4 w-4 mr-1" />
              {step > 1 ? 'Back' : 'Cancel'}
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
                Next
                <CaretRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} loading={isSubmitting} disabled={!canProceed()}>
                Add &amp; Verify
              </Button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Discard guard for Escape / Cancel / X on a dirty form. Portals to
          `document.body` after this modal's own portal content, so it paints on
          top within the same `z-[100]` modal band (§59). */}
      <ConfirmDialog
        open={showDiscard}
        title="Discard changes?"
        description="You have unsaved changes. Leaving now will discard them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="destructive"
        onConfirm={() => { setShowDiscard(false); onClose() }}
        onCancel={() => setShowDiscard(false)}
      />
    </>,
    document.body,
  )
}

export default AddSenderEmailModal
